/**
 * LAGET — startsidan bara den med full vy ser (manager och bookkeeper).
 *
 * Prototypens data-screen="lag". Också den en del av stommen: den är lagets rot
 * och bär de fyra kakel som leder till chefens skärmar.
 *
 * VARJE KAKEL BÄR EN SIFFRA. Appboken har en egen numrerad regel mot en meny:
 * "Rutan bär en siffra, inte bara ett namn. Utan siffran är kaklet en meny. Med
 * siffran är det en uppmaning." Talen är därför RIKTIGA och hämtade, aldrig
 * platshållare — och står det inget tal än står det heller ingen undertext.
 *
 * VARIFRÅN TALEN KOMMER, och varför just därifrån:
 *   Attestera     samma räkning som Attestera.tsx gör: WEEK_TRANSITIONS avgör om
 *                 'approve' är tillåten ur veckans FAKTISKA läge, och veckan ska
 *                 ha något att godkänna. Notissiffran är ANTAL SAKER SOM VÄNTAR
 *                 PÅ DIG (se Kakel.tsx) — alltså den och ingen total.
 *   Laget idag    samma definition som TODAY och LagetIdag.tsx: "rapporterat" =
 *                 minst en levande TIDRAD i dag, oavsett vem som skrev in den
 *                 (beslut 06). Ingen incheckning finns att räkna.
 *   Inhyrda       gästernas minuter i veckan och antalet MOTPARTER, alltså
 *                 distinkta guest_org_number — det är org.nr som står på
 *                 fakturan, inte personen (beslut 17).
 *   Att fakturera SERVERNS tal ur INVOICE_BASIS, aldrig ett vi räknat själva.
 *                 Beloppet vilar på de FRYSTA satserna på varje rad (beslut 15)
 *                 och på rundningsreglerna i company/amounts.ts. Bara `approved`
 *                 visas: beslut 05 förbjuder att attesterat och oattesterat
 *                 läggs ihop, och ett blandat tal på en startsida är precis det
 *                 slags tal som får en chef att fakturera timmar ingen godkänt.
 *
 * TVÅ HÄMTNINGAR, OCH DET ÄR MEDVETET. Kaklen behöver både firmans rader och
 * serverns belopp, och det finns ingen rutt som ger båda. Perioden för beloppet
 * är densamma som Att fakturera öppnar med, så att kaklet och skärmen under det
 * aldrig kan visa olika tal. När steg 5 äger den lokala databasen blir den
 * första hämtningen en läsning ur den — se LagetData.ts.
 *
 * "NY ARBETSORDER" ÄR CHEFENS, INTE BOKFÖRARENS. Bokföraren står i
 * FULL_VIEW_ROLES och SER hela laget, men han förekommer inte i EN ENDA rad i
 * SYNC_RULES (beslut 07) — 'put:work_order' är MANAGER_ONLY. Knappen visas
 * därför bara för `arChef`. Samma regel gäller varje knapp på lagets skärmar:
 * kanSeLaget för att VISA, arChef för att låta GÖRA.
 *
 * LÅSLÄGET STÄNGER SKRIVNINGEN, INTE LÄSNINGEN (beslut 11): allt syns och kan
 * exporteras, inget nytt skapas eller ändras. Därför är knappen av när
 * prenumerationen är 'locked' medan skärmarna under kaklen fortsätter fungera.
 */

import { useEffect, useMemo, useState } from 'react';
import { INVOICE_BASIS, WEEK_TRANSITIONS } from '@delat/routes';
import type { Ore, WeekState } from '@delat/types';
import { Datumrad, Kakel, Kakelrutnat, Knapp, Lagesvaxel, Tomtext } from '../ui';
import { klienten } from '../api';
import { granskaFakturaunderlag } from '../api/granskare/laget';
import { useInloggad } from '../app/session';
import { useNavigering } from '../app/navigering';
import { dagarIVecka, idag, veckanNu, visaMinuter, visaOren } from '../app/datum';
import { periodFor, useFirmadata } from './LagetData';
import { Prenumerationsbanner } from './Prenumerationsbanner';

/**
 * Får 'approve' göras ur det läge veckan FAKTISKT står i? Frågan ställs till
 * WEEK_TRANSITIONS och besvaras aldrig av en avskrift — samma hjälpare som
 * Attestera.tsx, så att kaklets siffra och kön under det inte kan säga olika.
 */
const kanGodkannas = (lage: WeekState): boolean =>
  (WEEK_TRANSITIONS.approve.from as readonly WeekState[]).includes(lage);

export const Laget = () => {
  const { firmaId, aktiv, arChef, arLast } = useInloggad();
  const { ga } = useNavigering();
  const lage = useFirmadata(firmaId, 0);

  const nu = idag();
  const vecka = veckanNu();
  const dagarna = useMemo(() => dagarIVecka(vecka), [vecka]);

  /* --- Tre av talen ur firmans egna rader ------------------------------- */

  const tal = useMemo(() => {
    if (lage.status !== 'klar') return null;
    const d = lage.data;

    // Ingen av räkningarna gäller bokföraren: han rapporterar ingenting och
    // står inte i en enda rad i SYNC_RULES (beslut 07).
    const rapporterande = d.medlemmar.filter((m) => m.role !== 'bookkeeper');
    const veckanslage = new Map(
      d.veckolagen.filter((v) => v.iso_week === vecka).map((v) => [v.account_id as string, v.state]),
    );
    const iVeckan = <T extends { readonly work_date: string }>(rader: readonly T[]): readonly T[] =>
      rader.filter((r) => (dagarna as readonly string[]).includes(r.work_date));

    const tidIVeckan = iVeckan(d.tidrader);
    const milIVeckan = iVeckan(d.milrader);
    const utlaggIVeckan = iVeckan(d.utlagg);

    // ATTESTERA: en vecka väntar när övergången är tillåten OCH det finns något
    // att godkänna. En tom vecka är ingen uppmaning.
    const vantande = rapporterande.filter((m) => {
      const veckoLage = veckanslage.get(m.account_id) ?? 'open';
      if (!kanGodkannas(veckoLage)) return false;
      const harNagot =
        tidIVeckan.some((t) => t.account_id === m.account_id) ||
        milIVeckan.some((x) => x.account_id === m.account_id) ||
        utlaggIVeckan.some((u) => u.account_id === m.account_id) ||
        veckanslage.has(m.account_id);
      return harNagot;
    }).length;

    // LAGET IDAG: beslut 06 — minst en levande tidrad i dag, oavsett skrivare.
    const idagsrader = d.tidrader.filter((t) => t.work_date === nu);
    const rapporterat = new Set(idagsrader.map((t) => t.account_id as string)).size;

    // INHYRDA: minuter i veckan, och antalet MOTPARTER (org.nr, inte personer).
    const gastkonton = new Set(
      d.medlemmar.filter((m) => m.role === 'guest').map((m) => m.account_id as string),
    );
    const gastminuter = tidIVeckan
      .filter((t) => gastkonton.has(t.account_id))
      .reduce((s, t) => s + Number(t.minutes), 0);
    const motparter = new Set(
      d.medlemmar
        .filter((m) => m.role === 'guest' && m.guest_org_number !== null)
        .map((m) => m.guest_org_number as string),
    ).size;

    return {
      vantande,
      rapporterat,
      laget: rapporterande.length,
      gastminuter,
      motparter,
      harGaster: gastkonton.size > 0,
    };
  }, [lage, vecka, dagarna, nu]);

  /* --- Det fjärde talet ur SERVERN. Aldrig räknat här (beslut 15). ------- */

  const [attesteratOre, sattAttesteratOre] = useState<Ore | null>(null);

  useEffect(() => {
    const styrning = new AbortController();
    let aktuell = true;
    // Samma period som Att fakturera öppnar med, så att kaklet och skärmen
    // under det aldrig visar olika tal.
    const intervall = periodFor('12v');

    void (async () => {
      const svar = await klienten.anrop(
        INVOICE_BASIS,
        {
          params: { company_id: firmaId },
          data: {
            from_date: intervall.from_date,
            to_date: intervall.to_date,
            invoiced_filter: 'unbilled',
          },
          signal: styrning.signal,
        },
        granskaFakturaunderlag,
      );
      if (!aktuell || !svar.ok) return;
      sattAttesteratOre(svar.varde.approved.total_ore);
    })();

    return () => {
      aktuell = false;
      styrning.abort();
    };
  }, [firmaId]);

  /* --- Undertexterna. Utan hämtat tal står det ingenting. ---------------- */

  const attesttext =
    tal === null
      ? undefined
      : tal.vantande === 0
        ? 'Inget väntar på dig'
        : tal.vantande === 1
          ? '1 vecka väntar på dig'
          : `${tal.vantande} veckor väntar på dig`;

  const idagtext =
    tal === null ? undefined : `${tal.rapporterat} av ${tal.laget} har rapporterat`;

  const inhyrdtext =
    tal === null
      ? undefined
      : !tal.harGaster
        ? 'Ingen inhyrd upplagd'
        : tal.motparter === 1
          ? `${visaMinuter(tal.gastminuter)} i veckan · 1 företag`
          : `${visaMinuter(tal.gastminuter)} i veckan · ${tal.motparter} företag`;

  const fakturatext =
    attesteratOre === null ? undefined : `${visaOren(attesteratOre)} attesterat, ex moms`;

  return (
    <>
      <Lagesvaxel />
      <Datumrad stark={aktiv.name}>
        {arChef ? 'du är chef här' : 'du läser som bokförare'}
      </Datumrad>

      {/* Samma banner som på Mitt jobb, och den visar sig bara för chefen. En
          bokförare ser den aldrig: rutterna som betalar kräver ägaren (beslut
          14), så beskedet vore ett han inte kan agera på. */}
      <Prenumerationsbanner />

      <Kakelrutnat>
        <Kakel
          ikon="attest"
          titel="Attestera"
          text={attesttext}
          notis={tal?.vantande}
          till="attest"
        />
        <Kakel ikon="var" titel="Laget idag" text={idagtext} till="var" />
        <Kakel ikon="inhyrda" titel="Inhyrda" text={inhyrdtext} till="inhyrda" />
        <Kakel ikon="fakturera" titel="Att fakturera" text={fakturatext} till="fakturera" />
      </Kakelrutnat>

      {arChef && (
        <div style={{ marginTop: '0.75rem' }}>
          <Knapp
            disabled={arLast}
            onClick={() => ga('ordny')}
            title={arLast ? 'Prenumerationen är pausad. Inget nytt kan sparas.' : undefined}
          >
            Ny arbetsorder
          </Knapp>
        </div>
      )}

      <Tomtext>
        {arChef
          ? 'Attesterad tid blir fakturaunderlag samma sekund. Attest sker per vecka.'
          : 'Du läser allt och ändrar ingenting. Attest och ändringar gör chefen.'}
      </Tomtext>
    </>
  );
};
